import { useState, type ReactNode } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, Images, Home } from "lucide-react";

type AccentColor = "violet" | "blue";

const ACCENT_BORDER: Record<AccentColor, string> = {
  violet: "border-violet-500",
  blue: "border-blue-500",
};

const ACCENT_LIGHTBOX_BORDER: Record<AccentColor, string> = {
  violet: "border-violet-400",
  blue: "border-blue-400",
};

interface ListingPhotoGalleryProps {
  photos: string[];
  testIdPrefix: string;
  alt: string;
  heroHeight?: string;
  accent?: AccentColor;
  fallback?: ReactNode;
  overlay?: ReactNode;
  bottomOverlay?: ReactNode;
  showThumbnails?: boolean;
  thumbnailContainerClassName?: string;
  onHeroClick?: () => void;
  rounded?: boolean;
}

export function ListingPhotoGallery({
  photos: photosInput,
  testIdPrefix,
  alt,
  heroHeight = "h-48",
  accent = "blue",
  fallback,
  overlay,
  bottomOverlay,
  showThumbnails = true,
  thumbnailContainerClassName = "px-4 pt-3",
  onHeroClick,
  rounded = false,
}: ListingPhotoGalleryProps) {
  const photos = (photosInput ?? []).filter(Boolean);
  const hasPhotos = photos.length > 0;
  const hasMany = photos.length > 1;
  const [photoIdx, setPhotoIdx] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const safeIdx = Math.min(photoIdx, Math.max(photos.length - 1, 0));
  const goPrev = () => setPhotoIdx((i) => (i - 1 + photos.length) % photos.length);
  const goNext = () => setPhotoIdx((i) => (i + 1) % photos.length);

  const openLightbox = () => {
    if (!hasPhotos) return;
    setLightboxOpen(true);
    onHeroClick?.();
  };

  const heroClasses = `relative bg-gradient-to-br from-slate-100 to-slate-200 ${heroHeight} flex items-center justify-center group/hero ${rounded ? "rounded-xl overflow-hidden" : "overflow-hidden"}`;

  return (
    <>
      <div className={heroClasses}>
        {hasPhotos ? (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); openLightbox(); }}
            className="w-full h-full block"
            aria-label="Open photo gallery"
            data-testid={`button-open-gallery-${testIdPrefix}`}
          >
            <img src={photos[safeIdx]} alt={`${alt} photo ${safeIdx + 1}`} className="w-full h-full object-cover" />
          </button>
        ) : (
          fallback ?? <Home className="h-12 w-12 text-slate-300" />
        )}
        {hasMany && (
          <>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); goPrev(); }}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-1.5 shadow opacity-0 group-hover/hero:opacity-100 transition-opacity"
              aria-label="Previous photo"
              data-testid={`button-prev-photo-${testIdPrefix}`}
            >
              <ChevronLeft className="h-4 w-4 text-gray-800" />
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); goNext(); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-1.5 shadow opacity-0 group-hover/hero:opacity-100 transition-opacity"
              aria-label="Next photo"
              data-testid={`button-next-photo-${testIdPrefix}`}
            >
              <ChevronRight className="h-4 w-4 text-gray-800" />
            </button>
            <div
              className="absolute bottom-2 right-2 bg-black/60 text-white text-xs font-medium rounded-full px-2 py-0.5 flex items-center gap-1"
              data-testid={`text-photo-count-${testIdPrefix}`}
            >
              <Images className="h-3 w-3" />
              {safeIdx + 1} / {photos.length}
            </div>
          </>
        )}
        {overlay}
        {bottomOverlay}
      </div>

      {showThumbnails && hasMany && (
        <div className={`flex gap-1.5 overflow-x-auto ${thumbnailContainerClassName}`} data-testid={`thumbnail-strip-${testIdPrefix}`}>
          {photos.slice(0, 6).map((p, i) => (
            <button
              key={`${p}-${i}`}
              type="button"
              onClick={(e) => { e.stopPropagation(); setPhotoIdx(i); }}
              className={`flex-shrink-0 h-12 w-16 rounded overflow-hidden border-2 transition-all ${
                i === safeIdx ? ACCENT_BORDER[accent] : "border-transparent opacity-70 hover:opacity-100"
              }`}
              aria-label={`Show photo ${i + 1}`}
              data-testid={`button-thumb-${testIdPrefix}-${i}`}
            >
              <img src={p} alt={`Thumbnail ${i + 1}`} className="w-full h-full object-cover" />
            </button>
          ))}
          {photos.length > 6 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); openLightbox(); }}
              className="flex-shrink-0 h-12 w-16 rounded bg-gray-100 hover:bg-gray-200 text-xs font-medium text-gray-700 flex items-center justify-center"
              data-testid={`button-more-photos-${testIdPrefix}`}
            >
              +{photos.length - 6}
            </button>
          )}
        </div>
      )}

      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent
          className="max-w-5xl p-0 bg-black border-0 overflow-hidden"
          data-testid={`lightbox-${testIdPrefix}`}
        >
          <DialogHeader className="sr-only">
            <DialogTitle>{alt} photos</DialogTitle>
            <DialogDescription>Photo {safeIdx + 1} of {photos.length}</DialogDescription>
          </DialogHeader>
          <div className="relative w-full bg-black flex items-center justify-center" style={{ minHeight: "60vh" }}>
            {hasPhotos && (
              <img
                src={photos[safeIdx]}
                alt={`${alt} photo ${safeIdx + 1}`}
                className="max-h-[80vh] max-w-full object-contain"
                data-testid={`lightbox-image-${testIdPrefix}`}
              />
            )}
            {hasMany && (
              <>
                <button
                  type="button"
                  onClick={goPrev}
                  className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 shadow"
                  aria-label="Previous photo"
                  data-testid={`button-lightbox-prev-${testIdPrefix}`}
                >
                  <ChevronLeft className="h-5 w-5 text-gray-900" />
                </button>
                <button
                  type="button"
                  onClick={goNext}
                  className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 shadow"
                  aria-label="Next photo"
                  data-testid={`button-lightbox-next-${testIdPrefix}`}
                >
                  <ChevronRight className="h-5 w-5 text-gray-900" />
                </button>
              </>
            )}
            {hasPhotos && (
              <div className="absolute top-3 left-3 bg-black/60 text-white text-xs font-medium rounded-full px-3 py-1">
                {safeIdx + 1} / {photos.length}
              </div>
            )}
          </div>
          {hasMany && (
            <div className="flex gap-2 p-3 bg-black/95 overflow-x-auto">
              {photos.map((p, i) => (
                <button
                  key={`lb-${p}-${i}`}
                  type="button"
                  onClick={() => setPhotoIdx(i)}
                  className={`flex-shrink-0 h-14 w-20 rounded overflow-hidden border-2 transition-all ${
                    i === safeIdx ? ACCENT_LIGHTBOX_BORDER[accent] : "border-transparent opacity-60 hover:opacity-100"
                  }`}
                  aria-label={`Show photo ${i + 1}`}
                  data-testid={`button-lightbox-thumb-${testIdPrefix}-${i}`}
                >
                  <img src={p} alt={`Thumbnail ${i + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
